import {
  type Catalog,
  createCatalog,
  type Locale,
  type ParameterlessKeyOf,
} from '@croissants/i18n';
import { createLocaleContext, type LocaleProviderProps } from '@croissants/ui';
import { en } from './en';

/** Internal Ops is English-only. The platform's other locales and menu data are independent.
 * The French slot aliases English solely to satisfy the shared translator contract;
 * no French interface catalogue is bundled in this app.
 */
export const internalCatalog: Catalog<typeof en> = createCatalog({ en, fr: en });

export type InternalMessageKey = keyof typeof en;

/** Keys that need no interpolation - the ones a static label may reference. */
export type InternalStaticKey = ParameterlessKeyOf<typeof en>;

/** The typed translator: an unknown key or a missing parameter is a compile error. */
type Translate = ReturnType<Catalog<typeof en>['translatorFor']>;

export interface InternalTranslators {
  readonly t: Translate;
  /**
   * Translator for keys assembled at runtime - a status enum, a liability matrix
   * row, a readiness condition. The completeness test asserts that every family
   * of keys reachable this way exists in English, which is what keeps this
   * escape hatch honest.
   */
  tKey(key: string, params?: Readonly<Record<string, string | number>>): string;
  /**
   * A wire CODE in the operator's language, or the code itself.
   *
   * `tKey` answers with the KEY when no catalogue carries it, which on the
   * activity feed would print `activity.audit.something.new` into a table cell,
   * so a missing key degrades to the raw code. check-activity-catalogue is
   * what keeps the families it reads from reaching that.
   *
   * NOT for alerts. An alert kind is a member of one closed list and is shown
   * as its title and then its code (`alertLabel`, alert-title.ts, founder
   * decision alert-titles = B): there is no raw-code fallback for an alert,
   * and check-activity-catalogue fails on an alert title key read anywhere else.
   */
  tCode(family: string, code: string): string;
}

function translatorsFor(locale: Locale): InternalTranslators {
  const t = internalCatalog.translatorFor(locale);
  const tKey = (key: string, params?: Readonly<Record<string, string | number>>): string =>
    (
      internalCatalog.t as unknown as (
        locale: Locale,
        key: string,
        params?: Readonly<Record<string, string | number>>,
      ) => string
    )(locale, key, params);
  return {
    t,
    tKey,
    tCode: (family, code) => {
      const key = `${family}.${code}`;
      return key in en ? tKey(key) : code;
    },
  };
}

const locale = createLocaleContext({
  storageKey: 'cib.internal.locale',
  defaultLocale: 'en',
  useBrowserLanguages: false,
  derive: translatorsFor,
});

/** Ignore legacy browser and server preferences; this rule applies only to Internal Ops. */
const englishStorage = { read: () => 'en', write: () => undefined };

export function LocaleProvider({ children }: LocaleProviderProps) {
  return <locale.LocaleProvider storage={englishStorage}>{children}</locale.LocaleProvider>;
}

export function useLocale() {
  const { t, tKey, tCode } = locale.useLocale();
  return { locale: 'en' as const, t, tKey, tCode };
}
export type LocaleContextValue = ReturnType<typeof useLocale>;
export const useT = locale.useT;
